import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getUserProfile } from '../lib/api-client';
import type { UserProfile } from '../types';

const ProfileContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const ProfileCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  marginBottom: theme.spacing(3),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  marginBottom: theme.spacing(2),
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  fontSize: '2.5rem',
}));

const EventsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  marginBottom: theme.spacing(3),
}));

export const UserDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { userid } = useParams<{ userid: string }>();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userid) {
        setError('User ID not provided');
        setLoading(false);
        return;
      }

      try {
        const userProfile = await getUserProfile(userid);
        if (userProfile) {
          setProfile(userProfile);
        } else {
          setError('User not found');
        }
      } catch (err: any) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userid]);

  if (loading) {
    return (
      <ProfileContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      </ProfileContainer>
    );
  }

  if (!profile) {
    return (
      <ProfileContainer>
        <Alert severity="error">Profile not found</Alert>
      </ProfileContainer>
    );
  }

  const userInitial = profile.email?.charAt(0).toUpperCase() || '?';

  return (
    <ProfileContainer maxWidth="md">
      {/* Header with Back Button */}
      <HeaderBox>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            {profile.displayName || 'User Profile'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            @{profile.username || 'N/A'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{
            borderColor: '#667eea',
            color: '#667eea',
            '&:hover': {
              background: 'rgba(102, 126, 234, 0.1)',
              borderColor: '#667eea',
            },
          }}
        >
          Back to Users
        </Button>
      </HeaderBox>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <ProfileCard>
        {/* Tabs Navigation */}
        {/* <Box
          sx={{ mb: 3, display: 'flex', gap: 1, flexDirection: 'row', justifyContent: 'center' }}
        >
          <Button
            variant={activeTab === 0 ? 'contained' : 'outlined'}
            onClick={() => setActiveTab(0)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: activeTab === 0 ? '#2c2c2c' : 'transparent',
              color: activeTab === 0 ? '#fff' : '#2c2c2c',
              borderColor: '#e0e0e0',
              '&:hover': {
                backgroundColor: activeTab === 0 ? '#1a1a1a' : '#f5f5f5',
                borderColor: '#e0e0e0',
              },
            }}
          >
            Profile
          </Button>
          <Button
            variant={activeTab === 1 ? 'contained' : 'outlined'}
            onClick={() => setActiveTab(1)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: activeTab === 1 ? '#2c2c2c' : 'transparent',
              color: activeTab === 1 ? '#fff' : '#2c2c2c',
              borderColor: '#e0e0e0',
              '&:hover': {
                backgroundColor: activeTab === 1 ? '#1a1a1a' : '#f5f5f5',
                borderColor: '#e0e0e0',
              },
            }}
          >
            Events
          </Button>
        </Box> */}

        {/* Profile Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={4}>
            {/* Avatar Section */}

            {/* Profile Details Section */}
            <Grid size={{ xs: 12, sm: 12 }}>
              <Box>
                <Grid
                  sx={{
                    textAlign: 'center',
                    flexDirection: 'column',
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <ProfileAvatar>{userInitial}</ProfileAvatar>
                  <Typography variant="body2" color="textSecondary">
                    {profile.email}
                  </Typography>
                </Grid>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      Bio
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 0 }}>
                      {profile.biography || 'No Bio provided.'}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      Display Name
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {profile.displayName || 'Not set'}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      Email
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {profile.email}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      Location
                    </Typography>
                    <Typography variant="body2">{profile.location || 'Not set'}</Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                      WSDCID
                    </Typography>
                    <Typography variant="body2">{profile.wsdcId || 'Not set'}</Typography>
                    {profile.divisions && profile.divisions.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                          Divisions
                        </Typography>
                        {profile.divisions.map((div, index) => (
                          <Typography variant="body2" key={index}>
                            {div.division} - {div.role}
                          </Typography>
                        ))}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Events Tab Content */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Events
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                {
                  id: 1,
                  name: 'West Coast Classic 2025',
                  date: 'Jan 15, 2025',
                  location: 'Los Angeles, CA',
                  status: 'Registered',
                },
                {
                  id: 2,
                  name: 'Phoenix Dance Festival',
                  date: 'Feb 22, 2025',
                  location: 'Phoenix, AZ',
                  status: 'Registered',
                },
                {
                  id: 3,
                  name: 'Seattle Swing Fest',
                  date: 'Mar 10, 2025',
                  location: 'Seattle, WA',
                  status: 'Interested',
                },
              ].map((event) => (
                <Card key={event.id} variant="outlined">
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {event.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                          {event.date}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          📍 {event.location}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: event.status === 'Registered' ? '#e8f5e9' : '#fff3e0',
                          color: event.status === 'Registered' ? '#2e7d32' : '#e65100',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {event.status}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}
      </ProfileCard>
    </ProfileContainer>
  );
};
